import * as THREE from 'three';
import { bakeGroupGeometry, createInstancedMesh, mergeBufferGeometries } from './batch';
import type { GlowSpot } from './glow';
import { mesh, rod } from './helpers';
import type { ChannelMaterials, ParkMaterials } from './materials';
import {
  bakedDecor,
  bulbTransforms,
  buntingSwag,
  createTwinkle,
  haloSpots,
  nightDressing,
  pennantRing,
  ringPositions,
  signMedallion,
  starTopper,
  visualTier,
} from './tiers';

const HORSE_COATS = [0xfff7ec, 0xd47a3c, 0x5a3824, 0xf0a0b4, 0xfff1d6, 0x8ab4e8, 0xe8c070, 0xb08ad8];
const HORSE_TRIMS = [0xe5514f, 0x4f9ddb, 0xf2c14e, 0x3fb8a8, 0x9a6fc9, 0xe5514f, 0x4f9ddb, 0xf2c14e];
const HORSE_COUNT = 8;
const HORSE_RADIUS = 1.4;
const CANOPY_RADIUS = 2.24;
const CANOPY_BASE = 2.18;
const WEDGES = 12;

function createHorsePrototype(mats: ParkMaterials, trim: THREE.Material): THREE.Group {
  const horse = new THREE.Group();
  const coat = mats.horseCoat;
  horse.add(mesh(new THREE.CapsuleGeometry(0.115, 0.3, 3, 8), coat, { position: [0, 0.42, 0], rotation: [0, 0, Math.PI / 2] }));
  horse.add(mesh(new THREE.SphereGeometry(0.125, 8, 6), coat, { position: [0.17, 0.44, 0] }));
  horse.add(mesh(new THREE.SphereGeometry(0.118, 8, 6), coat, { position: [-0.17, 0.42, 0] }));
  horse.add(mesh(new THREE.CapsuleGeometry(0.07, 0.2, 2, 7), coat, { position: [0.27, 0.62, 0], rotation: [0, 0, -0.75] }));
  horse.add(mesh(new THREE.CapsuleGeometry(0.055, 0.13, 2, 7), coat, { position: [0.4, 0.71, 0], rotation: [0, 0, -2.12] }));
  horse.add(mesh(new THREE.SphereGeometry(0.048, 7, 5), coat, { position: [0.47, 0.66, 0] }));
  for (const side of [-1, 1]) {
    horse.add(mesh(new THREE.ConeGeometry(0.022, 0.07, 4), coat, { position: [0.36, 0.82, side * 0.035], rotation: [side * 0.25, 0, 0.1] }));
  }
  horse.add(mesh(new THREE.CapsuleGeometry(0.03, 0.16, 2, 5), coat, { position: [-0.28, 0.33, 0], rotation: [0, 0, -0.64] }));
  const legs: Array<[number, number, number, number]> = [
    [0.15, 0.24, 0.075, -0.55],
    [0.13, 0.26, -0.075, -0.25],
    [-0.14, 0.2, 0.075, 0.42],
    [-0.15, 0.19, -0.075, 0.5],
  ];
  for (const [x, y, z, tilt] of legs) {
    horse.add(mesh(new THREE.CylinderGeometry(0.026, 0.03, 0.24, 5), coat, { position: [x, y, z], rotation: [0, 0, tilt] }));
  }
  horse.add(mesh(new THREE.CapsuleGeometry(0.036, 0.2, 2, 5), trim, { position: [0.2, 0.68, 0], rotation: [0, 0, -0.75] }));
  horse.add(mesh(new THREE.BoxGeometry(0.24, 0.03, 0.22), trim, { position: [0.02, 0.51, 0] }));
  horse.add(mesh(new THREE.BoxGeometry(0.2, 0.05, 0.16), trim, { position: [0.02, 0.545, 0] }));
  horse.add(mesh(new THREE.BoxGeometry(0.04, 0.1, 0.15), trim, { position: [-0.08, 0.6, 0] }));
  return horse;
}

const CANOPY_PROFILE: Array<[number, number]> = [
  [2.26, 0],
  [2.1, 0.14],
  [1.78, 0.36],
  [1.4, 0.62],
  [1.02, 0.88],
  [0.66, 1.12],
  [0.36, 1.32],
  [0.14, 1.44],
];

/** Level 3 crown: a second, smaller tent stacked on the main canopy. */
const CROWN_PROFILE: Array<[number, number]> = [
  [0.98, 1.3],
  [0.86, 1.42],
  [0.62, 1.62],
  [0.36, 1.82],
  [0.12, 1.96],
];

function mergeCanopyWedges(material: THREE.Material, parity: number, source: Array<[number, number]> = CANOPY_PROFILE): THREE.Mesh {
  const profile = source.map(([r, h]) => new THREE.Vector2(r, CANOPY_BASE + h));
  const pieces: THREE.BufferGeometry[] = [];
  for (let i = parity; i < WEDGES; i += 2) {
    pieces.push(new THREE.LatheGeometry(profile, 3, (i / WEDGES) * Math.PI * 2, (Math.PI * 2) / WEDGES));
  }
  const item = new THREE.Mesh(mergeBufferGeometries(pieces), material);
  pieces.forEach((geometry) => geometry.dispose());
  item.castShadow = true;
  item.receiveShadow = true;
  return item;
}

function ringTransforms(count: number, radius: number, y: number, parity?: number): Array<{ position: [number, number, number]; rotation: [number, number, number] }> {
  const items: Array<{ position: [number, number, number]; rotation: [number, number, number] }> = [];
  for (let i = 0; i < count; i += 1) {
    if (parity != null && i % 2 !== parity) continue;
    const angle = (i / count) * Math.PI * 2;
    items.push({
      position: [Math.cos(angle) * radius, y, Math.sin(angle) * radius],
      rotation: [0, Math.atan2(Math.cos(angle), Math.sin(angle)), 0],
    });
  }
  return items;
}

export interface CarouselOptions {
  /** Upgrade level (1–3). Drives the COL-011B visual tier; footprint is unchanged. */
  level?: number;
}

export function createCarousel(mats: ParkMaterials, channels: ChannelMaterials, options: CarouselOptions = {}): THREE.Group {
  const tier = visualTier(options.level);
  const root = new THREE.Group();
  const ride = new THREE.Group();
  const dummy = new THREE.Object3D();

  root.add(mesh(new THREE.CylinderGeometry(2.3, 2.4, 0.12, 32), channels.structure, { position: [0, 0.06, 0], receive: true }));
  root.add(mesh(new THREE.CylinderGeometry(2.04, 2.1, 0.18, 32), channels.structure, { position: [0, 0.21, 0] }));
  root.add(mesh(new THREE.BoxGeometry(0.62, 0.12, 1.0), channels.structure, { position: [2.5, 0.06, 0] }));
  root.add(mesh(new THREE.BoxGeometry(0.34, 0.12, 0.8), channels.structure, { position: [2.18, 0.18, 0] }));
  const gatePosts = new THREE.CylinderGeometry(0.03, 0.03, 0.72, 6);
  const gateLeft = gatePosts.clone().translate(2.36, 0.66, 0.44);
  const gateRight = gatePosts.clone().translate(2.36, 0.66, -0.44);
  const gateBar = new THREE.BoxGeometry(0.05, 0.05, 0.94).translate(2.36, 1.03, 0);
  root.add(mesh(mergeBufferGeometries([gateLeft, gateRight, gateBar]), channels.accent));

  ride.add(mesh(new THREE.CylinderGeometry(1.98, 1.98, 0.05, 32), channels.secondary, { position: [0, 0.325, 0] }));
  const rimRing = new THREE.TorusGeometry(2.0, 0.045, 8, 40);
  rimRing.rotateX(Math.PI / 2);
  rimRing.translate(0, 0.35, 0);
  ride.add(mesh(rimRing, channels.accent));
  ride.add(createInstancedMesh(new THREE.BoxGeometry(0.42, 0.13, 0.04), channels.primary, ringTransforms(16, 2.02, 0.22, 0)));
  ride.add(createInstancedMesh(new THREE.BoxGeometry(0.42, 0.13, 0.04), channels.secondary, ringTransforms(16, 2.02, 0.22, 1)));

  ride.add(mesh(new THREE.CylinderGeometry(0.5, 0.56, 1.9, 16), channels.secondary, { position: [0, 1.3, 0] }));
  ride.add(createInstancedMesh(new THREE.BoxGeometry(0.11, 1.5, 0.035), channels.accent, ringTransforms(8, 0.52, 1.25)));
  const collar = new THREE.TorusGeometry(0.54, 0.035, 8, 20);
  collar.rotateX(Math.PI / 2);
  const collarA = collar.clone().translate(0, 0.62, 0);
  const collarB = collar.clone().translate(0, 2.0, 0);
  const collarC = collar.clone().translate(0, 1.36, 0);
  ride.add(mesh(mergeBufferGeometries([collarA, collarB, collarC]), channels.accent));
  ride.add(mesh(new THREE.CylinderGeometry(0.12, 0.2, 1.5, 10), channels.accent, { position: [0, 2.95, 0] }));

  ride.add(mergeCanopyWedges(channels.primary, 1));
  ride.add(mergeCanopyWedges(channels.secondary, 0));
  const cornice = new THREE.TorusGeometry(CANOPY_RADIUS, 0.07, 8, 48);
  cornice.rotateX(Math.PI / 2);
  cornice.translate(0, CANOPY_BASE - 0.02, 0);
  ride.add(mesh(cornice, channels.accent));

  const scallop = new THREE.CircleGeometry(0.16, 8, Math.PI, Math.PI);
  ride.add(createInstancedMesh(scallop, channels.primary, ringTransforms(24, CANOPY_RADIUS + 0.01, CANOPY_BASE - 0.06, 0), { cast: false }));
  ride.add(createInstancedMesh(scallop, channels.secondary, ringTransforms(24, CANOPY_RADIUS + 0.01, CANOPY_BASE - 0.06, 1), { cast: false }));

  const bulbs = [...ringTransforms(24, CANOPY_RADIUS + 0.02, CANOPY_BASE + 0.1), ...ringTransforms(8, 0.56, 2.12)];
  const halos: GlowSpot[] = haloSpots(ringPositions(24, CANOPY_RADIUS + 0.02, CANOPY_BASE + 0.1), 0.62);

  if (tier >= 2) {
    // Level 2: gold ribs on the canopy seams, a pennant crown, deck-rim bulbs and an under-eave bulb row.
    const decor = new THREE.Group();
    for (let i = 0; i < WEDGES; i += 1) {
      const angle = (i / WEDGES) * Math.PI * 2;
      const outer = new THREE.Vector3(Math.cos(angle) * 2.22, CANOPY_BASE + 0.03, Math.sin(angle) * 2.22);
      const inner = new THREE.Vector3(Math.cos(angle) * 0.2, CANOPY_BASE + 1.42, Math.sin(angle) * 0.2);
      decor.add(rod(outer, inner, 0.05, mats.gold, 5));
    }
    decor.add(pennantRing(mats, 12, 1.42, CANOPY_BASE + 0.6, 0.3, Math.PI / WEDGES));
    if (tier === 2) {
      // gold crown ring around the finial so the upgrade reads from the top-down camera
      const topRing = new THREE.TorusGeometry(0.42, 0.05, 8, 24);
      topRing.rotateX(Math.PI / 2);
      topRing.translate(0, CANOPY_BASE + 1.3, 0);
      decor.add(mesh(topRing, mats.gold));
    }
    ride.add(bakedDecor(decor, mats));
    bulbs.push(...ringTransforms(16, 2.02, 0.42), ...ringTransforms(24, CANOPY_RADIUS + 0.02, CANOPY_BASE - 0.2, 1));
    halos.push(...haloSpots(ringPositions(16, 2.02, 0.42), 0.42));
  }

  let twinkle: ((elapsed: number) => void) | null = null;
  if (tier >= 3) {
    // Level 3: stacked crown tent, radial bunting swags, star topper and a twinkling crown bulb ring.
    ride.add(mergeCanopyWedges(channels.secondary, 1, CROWN_PROFILE));
    ride.add(mergeCanopyWedges(channels.primary, 0, CROWN_PROFILE));
    const crown = new THREE.Group();
    const crownRing = new THREE.TorusGeometry(0.98, 0.05, 8, 32);
    crownRing.rotateX(Math.PI / 2);
    crownRing.translate(0, CANOPY_BASE + 1.3, 0);
    crown.add(mesh(crownRing, mats.gold));
    for (let i = 0; i < 8; i += 1) {
      const angle = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const top = new THREE.Vector3(Math.cos(angle) * 0.96, CANOPY_BASE + 1.28, Math.sin(angle) * 0.96);
      const bottom = new THREE.Vector3(Math.cos(angle) * 2.18, CANOPY_BASE + 0.0, Math.sin(angle) * 2.18);
      crown.add(buntingSwag(mats, top, bottom, 5, 0.1));
    }
    crown.add(starTopper(mats, 0.4, CANOPY_BASE + 2.02));
    ride.add(bakedDecor(crown, mats));
    const crownBulbs = ringPositions(12, 1.0, CANOPY_BASE + 1.36);
    const crownBulbMesh = createInstancedMesh(new THREE.SphereGeometry(0.06, 8, 6), channels.lights, bulbTransforms(crownBulbs), {
      cast: false,
      receive: false,
    });
    ride.add(crownBulbMesh);
    twinkle = createTwinkle(crownBulbMesh, crownBulbs);
    halos.push(...haloSpots(crownBulbs, 0.5), { position: [0, CANOPY_BASE + 2.2, 0], size: 1.2 });
  } else {
    ride.add(mesh(new THREE.SphereGeometry(0.22, 12, 9), channels.accent, { position: [0, CANOPY_BASE + 1.56, 0] }));
    ride.add(mesh(new THREE.ConeGeometry(0.1, 0.36, 8), channels.primary, { position: [0, CANOPY_BASE + 1.86, 0] }));
  }
  ride.add(createInstancedMesh(new THREE.SphereGeometry(0.06, 8, 6), channels.lights, bulbs, { cast: false, receive: false }));

  if (tier >= 2) {
    // Entrance medallion sign over the gate (static, faces the gate direction) + night dressing.
    const gateSign = new THREE.Group();
    const arch = new THREE.TorusGeometry(0.47, 0.035, 6, 16, Math.PI);
    arch.rotateY(Math.PI / 2);
    arch.translate(2.36, 1.03, 0);
    gateSign.add(mesh(arch, mats.gold));
    const medallion = signMedallion(mats, tier >= 3 ? 0.34 : 0.28, mats.red);
    medallion.position.set(2.36, tier >= 3 ? 1.86 : 1.76, 0);
    medallion.rotation.y = Math.PI / 2;
    gateSign.add(medallion);
    if (tier >= 3) {
      gateSign.add(starTopper(mats, 0.14, 2.28).translateX(2.36));
    }
    root.add(bakedDecor(gateSign, mats));
    // pool sits just above the plinth top so it also clears the contextual lawn pad at 0.58 scale
    root.add(...nightDressing(mats, halos, { radius: tier >= 3 ? 4.0 : 3.3, y: 0.2 }));
  }

  const trimMaterial = mats.horseCoat.clone();
  const prototype = createHorsePrototype(mats, trimMaterial);
  const coatGeometry = bakeGroupGeometry(prototype, mats.horseCoat);
  const trimGeometry = bakeGroupGeometry(prototype, trimMaterial);
  const horseCoat = createInstancedMesh(coatGeometry, mats.horseCoat, HORSE_COATS.map((color) => ({ position: [0, 0, 0], color })));
  const horseTrim = createInstancedMesh(trimGeometry, trimMaterial, HORSE_TRIMS.map((color) => ({ position: [0, 0, 0], color })));
  const poles = createInstancedMesh(
    new THREE.CylinderGeometry(0.028, 0.028, 1.86, 7),
    channels.accent,
    HORSE_COATS.map(() => ({ position: [0, 0, 0] })),
  );
  ride.add(poles, horseCoat, horseTrim);

  root.add(ride);
  root.userData.update = (elapsed: number) => {
    ride.rotation.y = elapsed * 0.36;
    twinkle?.(elapsed);
    for (let i = 0; i < HORSE_COUNT; i += 1) {
      const angle = (i / HORSE_COUNT) * Math.PI * 2;
      const bob = 0.36 + Math.sin(elapsed * 2.1 + i * 0.9) * 0.11;
      dummy.position.set(Math.cos(angle) * HORSE_RADIUS, bob, Math.sin(angle) * HORSE_RADIUS);
      dummy.rotation.set(0, -angle + Math.PI / 2, 0);
      dummy.scale.setScalar(1);
      dummy.updateMatrix();
      horseCoat.setMatrixAt(i, dummy.matrix);
      horseTrim.setMatrixAt(i, dummy.matrix);
      dummy.position.y = 1.26;
      dummy.updateMatrix();
      poles.setMatrixAt(i, dummy.matrix);
    }
    horseCoat.instanceMatrix.needsUpdate = true;
    horseTrim.instanceMatrix.needsUpdate = true;
    poles.instanceMatrix.needsUpdate = true;
  };
  root.userData.update(0);
  return root;
}
